import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetAdminCronsStatusAPI } from '../../../../api/AdminAPI';

const AUTO_REFRESH_MS = 60000;

/**
 * useAdminCrons
 *
 * Hook responsável pela tela de Rotinas do Admin Geral: status de saúde de cada script
 * de cron (última execução, resultado, lock e artefato), lido de status.php. Sem
 * filtros — a lista completa é recarregada em silêncio a cada 60s.
 *
 * @returns {{
 *   routines: Array,
 *   generatedAt: string,
 *   daysScanned: number,
 *   logDirExists: boolean,
 *   stats: { total: number, withError: number, late: number, never: number, unscheduled: number },
 *   isLoading: boolean,
 *   error: string|null,
 *   refresh: Function,
 * }}
 */
function useAdminCrons() {
  const [routines, setRoutines] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [daysScanned, setDaysScanned] = useState(0);
  const [logDirExists, setLogDirExists] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback((silent = false) => {
    if (!silent) setIsLoading(true);
    setError(null);
    GetAdminCronsStatusAPI((response) => {
      if (!response || response.success === false) {
        setError(response?.error || 'Erro ao carregar o status das rotinas. Tente novamente.');
        setIsLoading(false);
        return;
      }
      setRoutines(response.routines || []);
      setGeneratedAt(response.generated_at || null);
      setDaysScanned(response.days_scanned || 0);
      setLogDirExists(response.log_dir_exists !== false);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchStatus(false);
  }, [fetchStatus]);

  useEffect(() => {
    const timer = setInterval(() => fetchStatus(true), AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  const stats = useMemo(() => ({
    total: routines.length,
    withError: routines.filter((r) => r.status === 'error' || r.status === 'stale_lock').length,
    late: routines.filter((r) => r.status === 'late').length,
    never: routines.filter((r) => r.status === 'never').length,
    unscheduled: routines.filter((r) => r.schedule === null).length,
  }), [routines]);

  return {
    routines,
    generatedAt,
    daysScanned,
    logDirExists,
    stats,
    isLoading,
    error,
    refresh: () => fetchStatus(false),
  };
}

export default useAdminCrons;
